import React, { useState, useEffect } from 'react';
import { passwordResetAdminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowsClockwise, CheckCircle, Copy, Trash, Key, Clock,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

export default function PasswordRequestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | approved | used
  const [approvedFor, setApprovedFor] = useState({}); // id -> {reset_link, token, expires_at}

  const isAdmin = user && user.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await passwordResetAdminApi.list(filter === 'all' ? null : filter);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, filter]);

  const approve = async (id) => {
    try {
      const { data } = await passwordResetAdminApi.approve(id);
      setApprovedFor((prev) => ({ ...prev, [id]: data }));
      toast.success('Reset link generated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    }
  };

  const remove = async (id) => {
    try {
      await passwordResetAdminApi.delete(id);
      toast.success('Request deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8">
        <h1 className="font-heading text-xl font-semibold">Password reset requests</h1>
        <p className="text-sm text-muted-foreground mt-2">Admin access required.</p>
      </div>
    );
  }

  const statusStyles = {
    pending:  { bg: 'bg-[#FFD100]/15', text: 'text-[#B5940A]' },
    approved: { bg: 'bg-[#41B6E6]/15', text: 'text-[#1E7CA6]' },
    used:     { bg: 'bg-success/15',    text: 'text-success' },
  };

  return (
    <div className="p-6 space-y-6" data-testid="password-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Password reset requests</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Review and approve password reset requests. Share the generated one-time link with the user out-of-band
            (chat, phone, in-person). Links expire in 1 hour.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-sm overflow-hidden border border-border" data-testid="status-filter">
            {['all', 'pending', 'approved', 'used'].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 text-xs capitalize ${filter === s ? 'bg-primary text-primary-foreground' : 'bg-card hover:bg-muted'}`}
                data-testid={`filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="reload-requests">
            <ArrowsClockwise size={14} className="mr-1.5" /> Reload
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-sm">
          No password reset requests.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const sty = statusStyles[r.status] || statusStyles.pending;
            const approved = approvedFor[r.id];
            return (
              <div key={r.id} className="bg-card border border-border rounded-sm p-4" data-testid={`request-${r.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-medium">{r.email}</span>
                      {!r.user_exists && (
                        <Badge variant="outline" className="text-xs border-destructive/40 text-destructive">No matching account</Badge>
                      )}
                      <Badge variant="outline" className={`text-xs capitalize ${sty.bg} ${sty.text} border-transparent`}>
                        {r.status}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Clock size={11} />Requested {new Date(r.requested_at).toLocaleString()}</span>
                      {r.user_name && <span>· {r.user_name}</span>}
                      {r.approved_at && <span>· approved {new Date(r.approved_at).toLocaleString()}</span>}
                      {r.expires_at && r.status === 'approved' && (
                        <span>· expires {new Date(r.expires_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status === 'pending' && r.user_exists && (
                      <Button
                        size="sm"
                        onClick={() => approve(r.id)}
                        className="rounded-sm"
                        style={{ background: '#002677' }}
                        data-testid={`approve-${r.id}`}
                      >
                        <Key size={14} className="mr-1.5" /> Generate reset link
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      onClick={() => remove(r.id)}
                      data-testid={`delete-${r.id}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>

                {/* Fresh approval — show raw link once */}
                {approved?.reset_link && (
                  <div className="mt-4 p-3 rounded-sm border border-success/30 bg-success/5 space-y-2" data-testid={`approved-link-${r.id}`}>
                    <div className="flex items-center gap-2 text-xs text-success font-semibold">
                      <CheckCircle size={14} weight="fill" /> One-time reset link generated — copy now, it won't be shown again
                    </div>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={approved.reset_link}
                        className="font-mono text-xs rounded-sm"
                        onFocus={(e) => e.target.select()}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => { navigator.clipboard.writeText(approved.reset_link); toast.success('Link copied'); }}
                        data-testid={`copy-link-${r.id}`}
                      >
                        <Copy size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
